import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/rpc';
import { queryKeys } from '../../api/queryKeys';
import type { Study, WhereBean } from '../../api/types';
import { useUIStore } from '../../stores/uiStore';
import { useAuth } from '../auth';

export interface StudyQueryParams {
  tab: string;
  page: number;
  pageSize: number;
  sortField?: string;
  sortDirection?: 'ASC' | 'DESC';
  filters?: Record<string, string[]>;
  quickSearch?: string;
  quickSearchField?: string;
}

/**
 * Convert user input to DICOM-format name search string.
 * Matches GWT NameSearchUtils.makeSmartNameSearchString().
 *
 * Examples:
 *   "HUBER"          → "HUBER*"
 *   "HUBER, MARIA"   → "HUBER^MARIA*"
 *   "HUBER,"         → "HUBER^*"
 */
function makeSmartNameSearchString(input: string): string {
  if (!input) return input;
  let sName = input.trim();
  if (!sName) return sName;

  let escape = false;
  if (sName.startsWith('\\\\')) {
    sName = sName.substring(1);
  } else if (sName.startsWith('\\')) {
    escape = true;
    sName = sName.substring(1);
  }

  if (sName.includes(',') && !escape) {
    // Comma-separated: parse as LastName, FirstName, MiddleName, etc.
    const parts = sName.split(',', 4).map((s) => s.trim());
    if (parts.length < 5 && sName.endsWith(',')) parts.push('');

    let result = '';
    for (let i = 0; i < parts.length; i++) {
      const s = parts[i];
      if (i < 5 && i > 0) result += '^';
      // Add wildcard before last middle/suffix part
      if (i > 1 && i === parts.length - 1 && s.length > 0 && s[0] !== '*') result += '*';
      // Empty part → add *
      if (s.length === 0 && ((result.length > 0 && result[result.length - 1] !== '*') || i === 0)) {
        result += '*';
      } else {
        result += s;
      }
      // Trailing wildcard on last part
      if (i === parts.length - 1 && result[result.length - 1] !== '*') result += '*';
    }
    // Strip trailing carets
    return result.replace(/\^+$/, '');
  }

  // No comma — simple append
  return sName.includes('*') ? sName : sName + '*';
}

/** Format a JS Date to "yyyy/MM/dd HH:mm" for backend dval parameters */
function formatDateForBackend(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Build the `where` array for getStudiesPaged.
 *
 * The backend `makeWhere()` in JSONRPCFilter expects objects with:
 *   attr — field name
 *   op   — Operator enum name (EQUALS, LIKE, IN, GREATERTHANEQUALS, LESSTHANEQUALS, etc.)
 *   sval — string values as JSON array
 *   dval — date values as JSON array (format "yyyy/MM/dd HH:mm")
 */
function buildWhereClause(params: StudyQueryParams, advancedFilters: WhereBean[], userObjKey?: string): WhereBean[] {
  const where: WhereBean[] = [];

  // Tab-based filters (matching GWT Query.CommonQueries)
  switch (params.tab) {
    case 'newstudy':
      where.push({ attr: 'status', op: 'EQUALS', sval: ['NEWSTUDY'] });
      break;
    case 'assigned':
      where.push({ attr: 'status', op: 'IN', sval: ['INPROGRESS', 'NEWSTUDY', 'ASSIGNED'] });
      where.push({ attr: 'radiologist', op: 'ISNOTNULL' });
      break;
    case 'dictated':
      where.push({ attr: 'status', op: 'EQUALS', sval: ['DICTATED'] });
      break;
    case 'read':
      where.push({ attr: 'status', op: 'EQUALS', sval: ['READ'] });
      break;
    case 'urgent':
      where.push({ attr: 'urgentActionNeeded', op: 'EQUALS', sval: ['true'] });
      break;
    case 'onhold':
      where.push({ attr: 'wasHeld', op: 'EQUALS', sval: ['true'] });
      break;
    case 'unassigned':
      where.push({ attr: 'status', op: 'EQUALS', sval: ['NEWSTUDY'] });
      where.push({ attr: 'radiologist', op: 'ISNULL' });
      break;
    case 'inprogress':
      where.push({ attr: 'status', op: 'EQUALS', sval: ['INPROGRESS'] });
      break;
    case 'delivered':
      where.push({ attr: 'status', op: 'EQUALS', sval: ['DELIVERED'] });
      break;
    case 'transcribed':
      where.push({ attr: 'status', op: 'EQUALS', sval: ['TRANSCRIBED'] });
      break;
    case 'ordered':
      where.push({ attr: 'status', op: 'EQUALS', sval: ['ORDERED'] });
      break;
    case 'scheduled':
      where.push({ attr: 'status', op: 'EQUALS', sval: ['SCHEDULED'] });
      break;

    // ── Global / Admin tabs ──
    case 'actionneeded':
      where.push({ attr: 'actionNeeded', op: 'EQUALS', sval: ['true'] });
      break;
    case 'discordance':
      where.push({ attr: 'discordanceActionNeeded', op: 'EQUALS', sval: ['true'] });
      break;
    case 'peerreview':
      if (userObjKey) {
        where.push({ attr: 'peerReviewActionNeeded', op: 'EQUALS', sval: ['true'] });
        where.push({ attr: 'radiologist', op: 'NOTEQUALS', sval: [userObjKey] });
      } else {
        where.push({ attr: 'peerReviewActionNeeded', op: 'EQUALS', sval: ['true'] });
      }
      break;
    case 'queued':
      where.push({ attr: 'status', op: 'IN', sval: ['INPROGRESS', 'NEWSTUDY', 'ASSIGNED'] });
      where.push({ attr: 'radiologist', op: 'ISNOTNULL' });
      break;
    case 'sent':
      if (userObjKey) {
        where.push({ attr: 'ae', op: 'EQUALS', sval: [userObjKey] });
      }
      break;
    case 'hasquiz':
      where.push({ attr: 'hasQuiz', op: 'EQUALS', sval: ['true'] });
      break;
    case 'qcall':
      where.push({ attr: 'qcstatus', op: 'IN', sval: ['NEED_QC', 'OPENED_QC'] });
      break;
    case 'unsigned':
      where.push({ attr: 'hasUnsigned', op: 'EQUALS', sval: ['true'] });
      break;
    case 'signed':
      where.push({ attr: 'hasSigned', op: 'EQUALS', sval: ['true'] });
      break;

    // ── "My" radiologist tabs ──
    case 'myactionneeded':
      where.push({ attr: 'actionNeeded', op: 'EQUALS', sval: ['true'] });
      if (userObjKey) where.push({ attr: 'radiologist', op: 'EQUALS', sval: [userObjKey] });
      break;
    case 'myqueued':
      where.push({ attr: 'status', op: 'IN', sval: ['INPROGRESS', 'NEWSTUDY', 'ASSIGNED'] });
      if (userObjKey) where.push({ attr: 'radiologist', op: 'EQUALS', sval: [userObjKey] });
      break;
    case 'mydelivered':
      where.push({ attr: 'status', op: 'EQUALS', sval: ['DELIVERED'] });
      if (userObjKey) where.push({ attr: 'radiologist', op: 'EQUALS', sval: [userObjKey] });
      where.push({ attr: 'wasHeld', op: 'ISNOTTRUE' });
      break;
    case 'myread':
      where.push({ attr: 'status', op: 'EQUALS', sval: ['READ'] });
      if (userObjKey) where.push({ attr: 'radiologist', op: 'EQUALS', sval: [userObjKey] });
      break;
    case 'mydictated':
      where.push({ attr: 'status', op: 'EQUALS', sval: ['DICTATED'] });
      if (userObjKey) where.push({ attr: 'radiologist', op: 'EQUALS', sval: [userObjKey] });
      break;
    case 'myunsigned':
      where.push({ attr: 'hasUnsigned', op: 'EQUALS', sval: ['true'] });
      if (userObjKey) where.push({ attr: 'radiologist', op: 'EQUALS', sval: [userObjKey] });
      break;
    case 'mysigned':
      if (userObjKey) where.push({ attr: 'radiologist', op: 'EQUALS', sval: [userObjKey] });
      where.push({ attr: 'hasSigned', op: 'EQUALS', sval: ['true'] });
      break;

    // ── Physician tab ──
    case 'mypatients':
      if (userObjKey) where.push({ attr: 'phy', op: 'EQUALS', sval: [userObjKey] });
      break;

    // ── Resident tabs (current user is resident) ──
    case 'myqueued_resident':
      where.push({ attr: 'status', op: 'IN', sval: ['INPROGRESS', 'NEWSTUDY', 'ASSIGNED', 'DELIVERED', 'COMPONLY', 'READ'] });
      if (userObjKey) where.push({ attr: 'resident', op: 'EQUALS', sval: [userObjKey] });
      where.push({ attr: 'hasmyresidentreports', op: 'NOTEQUALS', sval: ['true'] });
      break;
    case 'myreported_resident':
      where.push({ attr: 'status', op: 'IN', sval: ['DRAFT', 'DELIVERED', 'REPORTED', 'COMPONLY', 'ASSIGNED', 'READ'] });
      if (userObjKey) where.push({ attr: 'resident', op: 'EQUALS', sval: [userObjKey] });
      where.push({ attr: 'resScore', op: 'ISNULL' });
      where.push({ attr: 'hasmyresidentreports', op: 'EQUALS', sval: ['true'] });
      break;
    case 'myscored_resident':
      where.push({ attr: 'status', op: 'IN', sval: ['DRAFT', 'DELIVERED', 'REPORTED', 'COMPONLY', 'ASSIGNED', 'READ'] });
      if (userObjKey) where.push({ attr: 'resident', op: 'EQUALS', sval: [userObjKey] });
      where.push({ attr: 'resScore', op: 'ISNOTNULL' });
      break;

    // ── Attending → Resident tabs (current user is attending) ──
    // Note: these require the attending's resident list; for now, filter by resident ISNOTNULL
    // This will be enhanced when we add resident-list management
    case 'myresident_assigned':
      where.push({ attr: 'status', op: 'IN', sval: ['INPROGRESS', 'NEWSTUDY', 'ASSIGNED', 'DELIVERED', 'COMPONLY', 'READ'] });
      where.push({ attr: 'resident', op: 'ISNOTNULL' });
      where.push({ attr: 'myresidentshasreports', op: 'NOTEQUALS', sval: ['true'] });
      break;
    case 'myresident_reported':
      where.push({ attr: 'status', op: 'IN', sval: ['DRAFT', 'DELIVERED', 'REPORTED', 'COMPONLY', 'ASSIGNED', 'READ'] });
      where.push({ attr: 'resident', op: 'ISNOTNULL' });
      where.push({ attr: 'resScore', op: 'ISNULL' });
      where.push({ attr: 'myresidentshasreports', op: 'EQUALS', sval: ['true'] });
      break;
    case 'myresident_scored':
      where.push({ attr: 'status', op: 'IN', sval: ['DRAFT', 'DELIVERED', 'REPORTED', 'COMPONLY', 'ASSIGNED', 'READ'] });
      where.push({ attr: 'resident', op: 'ISNOTNULL' });
      where.push({ attr: 'resScore', op: 'ISNOTNULL' });
      break;

    // ── Additional tabs ──
    case 'myunread':
      where.push({ attr: 'status', op: 'IN', sval: ['INPROGRESS', 'NEWSTUDY', 'ASSIGNED', 'DELIVERED'] });
      if (userObjKey) where.push({ attr: 'radiologist', op: 'EQUALS', sval: [userObjKey] });
      break;
    case 'unread':
      where.push({ attr: 'status', op: 'IN', sval: ['INPROGRESS', 'NEWSTUDY', 'ASSIGNED', 'DELIVERED'] });
      break;
    case 'today': {
      const todayMidnight = new Date();
      todayMidnight.setHours(0, 0, 0, 0);
      where.push({ attr: 'updated', op: 'GREATERTHANEQUALS', dval: [formatDateForBackend(todayMidnight)] });
      break;
    }
    case 'lastweek': {
      const weekAgo = new Date();
      weekAgo.setHours(0, 0, 0, 0);
      weekAgo.setTime(weekAgo.getTime() - 7 * 86400000);
      where.push({ attr: 'updated', op: 'GREATERTHANEQUALS', dval: [formatDateForBackend(weekAgo)] });
      break;
    }
    case 'accessed':
      if (userObjKey) where.push({ attr: 'events', op: 'LIKE', sval: [`%"${userObjKey}"%`] });
      break;
    case 'has_bi_rads':
      where.push({ attr: 'biRads', op: 'ISNOTNULL', sval: ['true'] });
      break;
    case 'shared_by_me':
      if (userObjKey) where.push({ attr: 'shr', op: 'EQUALS', sval: [userObjKey] });
      break;
    case 'shared_with_me':
      if (userObjKey) where.push({ attr: 'she', op: 'EQUALS', sval: [userObjKey] });
      break;
    case 'ssl_late':
      where.push({ attr: 'slastatus', op: 'EQUALS', sval: ['LATE'] });
      break;

    // 'studies' = all, no filter
    // 'mystudies' = no frontend filter (server restricts per user)
    // 'url' = no frontend filter (server restricts per URL params)
  }

  // Aggregation filters (facet selections)
  if (params.filters) {
    for (const [field, values] of Object.entries(params.filters)) {
      if (values.length > 0) {
        where.push({ attr: field, op: 'IN', sval: values });
      }
    }
  }

  // Quick search (only if no advanced filters are active)
  // Matches GWT QuickSearchPanel.performSearch() logic.
  if (advancedFilters.length === 0 && params.quickSearch && params.quickSearch.trim()) {
    const searchValue = params.quickSearch.trim().toUpperCase();
    switch (params.quickSearchField) {
      case 'name':
        where.push({ attr: 'name', op: 'LIKE', sval: [makeSmartNameSearchString(searchValue)] });
        break;
      case 'id':
        where.push({ attr: 'id', op: 'LIKE', sval: [searchValue] });
        break;
      case 'acc':
        where.push({ attr: 'acc', op: 'LIKE', sval: [searchValue] });
        break;
      default:
        // 'smart' — search across name, id, acc with OR (matching GWT)
        where.push({ attr: 'name', op: 'LIKE', sval: [makeSmartNameSearchString(searchValue)] });
        where.push({ attr: '', op: 'OR' });
        where.push({ attr: 'id', op: 'LIKE', sval: [searchValue] });
        where.push({ attr: '', op: 'OR' });
        where.push({ attr: 'acc', op: 'LIKE', sval: [searchValue] });
        break;
    }
  }

  // Merge advanced search filters
  if (advancedFilters.length > 0) {
    where.push(...advancedFilters);
  }

  return where;
}

/**
 * TanStack Query hook for fetching paged studies.
 *
 * Uses server-side pagination and sorting via getStudiesPaged JSON-RPC.
 * Sorting is done by adding ORDER WhereBeans — the backend's makeWhere()
 * supports the ORDER operator which maps to SQL ORDER BY.
 */
export function useStudies(params: StudyQueryParams) {
  const start = params.page * params.pageSize;
  const advancedFilters = useUIStore((s) => s.advancedFilters);
  const { user } = useAuth();
  const userObjKey = user?.objKey;
  const where = buildWhereClause(params, advancedFilters, userObjKey);

  // Add server-side sorting via ORDER WhereBeans
  if (params.sortField) {
    where.push({
      attr: params.sortField,
      op: 'ORDER',
      sval: [params.sortDirection ?? 'ASC'],
    });
  }

  return useQuery({
    queryKey: queryKeys.studies.paged({
      tab: params.tab,
      page: params.page,
      pageSize: params.pageSize,
      sortField: params.sortField,
      sortDirection: params.sortDirection,
      filters: params.filters,
      quickSearch: params.quickSearch,
      quickSearchField: params.quickSearchField,
      advancedFilters,
    }),
    queryFn: ({ signal }) =>
      api.getStudiesPaged(start, params.pageSize, { where }, undefined, signal),
    staleTime: 30_000,
    refetchInterval: 60_000, // Auto-refresh every 60s like GWT
  });
}

/**
 * Sort studies client-side — fallback for fields not supported by SQL ORDER.
 * Prefer server-side sorting via ORDER WhereBeans in useStudies() above.
 */
export function sortStudiesClientSide(
  studies: Study[],
  field: string,
  direction: 'ASC' | 'DESC',
): Study[] {
  const sorted = [...studies].sort((a, b) => {
    const aVal = (a as unknown as Record<string, unknown>)[field];
    const bVal = (b as unknown as Record<string, unknown>)[field];

    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    const aStr = String(aVal);
    const bStr = String(bVal);

    // Try numeric comparison for count fields
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return aVal - bVal;
    }

    return aStr.localeCompare(bStr, undefined, { sensitivity: 'base' });
  });

  if (direction === 'DESC') sorted.reverse();
  return sorted;
}

/**
 * Hook to manually refresh studies data.
 */
export function useRefreshStudies() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: queryKeys.studies.all });
}
